import { FileCheck, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(n ?? 0));
}

/**
 * CreateInvoicePreview — rich two-step approval card for CREATE_INVOICE actions.
 *
 * Replaces the generic key-value PendingActionCard for invoice creation:
 * shows a proper line-items table, client name, subtotal/IVA/total breakdown,
 * and type-appropriate approve/reject buttons.
 *
 * Props: action { type, preview }, onApprove, onReject, status
 */
export default function CreateInvoicePreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <FileCheck className="w-3.5 h-3.5" />
        <span>Factura aprobada — creando borrador…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Creación de factura cancelada</span>
      </div>
    );
  }

  const conceptos = preview?.conceptos ?? [];

  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20 p-4 space-y-3 my-2 max-w-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <Badge variant="outline" className="text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 text-[10px] mb-1">
            Requiere aprobación
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight">
            {preview?.cliente ?? '—'}
          </p>
        </div>
        <span className="text-lg font-bold text-indigo-700 dark:text-indigo-300 shrink-0 tabular-nums">
          {formatEur(preview?.total)}
        </span>
      </div>

      {/* Line items table */}
      {conceptos.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-indigo-200 dark:border-indigo-800 text-muted-foreground">
                <th className="text-left pb-1 font-medium">Concepto</th>
                <th className="text-right pb-1 font-medium">Ud.</th>
                <th className="text-right pb-1 font-medium">P.U.</th>
                <th className="text-right pb-1 font-medium">IVA</th>
                <th className="text-right pb-1 font-medium">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-indigo-100 dark:divide-indigo-900/40">
              {conceptos.map((c, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2 text-foreground max-w-[120px] truncate">{c.descripcion}</td>
                  <td className="py-1 text-right text-muted-foreground">{c.cantidad}</td>
                  <td className="py-1 text-right text-muted-foreground">{c.precio_unitario}€</td>
                  <td className="py-1 text-right text-muted-foreground">{c.iva}</td>
                  <td className="py-1 text-right font-medium text-foreground tabular-nums">{c.total_linea}€</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Totals */}
      <div className="flex flex-col gap-0.5 text-xs border-t border-indigo-200 dark:border-indigo-800 pt-2">
        <div className="flex justify-between text-muted-foreground">
          <span>Base imponible</span>
          <span>{formatEur(preview?.subtotal)}</span>
        </div>
        <div className="flex justify-between text-muted-foreground">
          <span>IVA</span>
          <span>{formatEur(preview?.impuestos)}</span>
        </div>
        <div className="flex justify-between font-semibold text-foreground">
          <span>Total</span>
          <span>{formatEur(preview?.total)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject}
          className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        <Button size="sm" onClick={onApprove}
          className="flex-1 h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
          <FileCheck className="w-3 h-3 mr-1" />Crear factura
        </Button>
      </div>
    </div>
  );
}
